import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, OnDestroy, Output, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AngularMaterialModule } from 'src/app/shared/module/angular-material.module';
import { AlertDetailService } from '../alert-detail/alert-detail.service';
import { Alert } from 'src/app/models/alert';
import { take, takeUntil, switchMap, filter, map, defaultIfEmpty, catchError } from 'rxjs/operators';
import { Select } from '@ngxs/store';
import { TenantSessionState } from 'src/app/store/state/tenant-session.state';
import { TenantUser } from 'src/app/models/tenantUser';
import { Observable, Subject, timer } from 'rxjs';
import { AlertService } from '../alert/alert.service';
import { ReferenceData } from 'src/app/models/referenceData';
import { UserService } from 'src/app/users/user.service';
import { AuthService } from 'src/app/services/auth.service';
import { AuthorizationService } from 'src/app/services/authorization.service';
import { MaintainTenant, MaintainVehicle } from 'src/app/models/constant/right';
import { CaseWorkflowContext, alertCaseWorkflowContext } from './case-workflow-context';
import { FilterDropdownComponent, FilterDropdownOption } from 'src/app/shared/components/filter-dropdown/filter-dropdown.component';

@Component({
  selector: 'app-alert-case',
  standalone: true,
  imports: [CommonModule, TranslateModule, AngularMaterialModule, FormsModule, FilterDropdownComponent],
  templateUrl: './alert-case.component.html',
  styleUrl: './alert-case.component.css'
})
export class AlertCaseComponent implements OnChanges, OnInit, OnDestroy {
  @Input() alert: Alert;
  // Origin strategy (active-case lookup, create body, assignee filter). When
  // omitted, an alert-flavored context is built from `alert` so the existing
  // drawer call sites keep working unchanged.
  @Input() context: CaseWorkflowContext;
  // Inline (accordion) hosting: hides the drawer-only Close button and drops
  // the drawer's viewport-height sizing.
  @Input() embedded = false;
  // An active-case read the host already warmed, so the panel paints without a round trip.
  // Optional — the drawer call sites omit it and fetch on mount.
  @Input() preload$?: Observable<any>;
  @Output() close$ = new EventEmitter<boolean>();
  // What the last accepted write asserted about the case. A host that projects the
  // Issue row patches from this — the projection lags the write, so waiting for it
  // leaves the screen stale for the whole safety lag. The case id rides along because a
  // row that shows the new assignee but still says "no case" is a half-applied claim.
  @Output() caseWritten = new EventEmitter<{
    caseId: string; assignedUserId: string; assignedLocationId: string;
  }>();

  @Select(TenantSessionState.getCurrentUser) getCurrentUser$;
  @Select(TenantSessionState.getCurrentTenantUsers) currentTenantUsers$: Observable<TenantUser[]>;
  @ViewChild('chatScrollArea') chatScrollArea: ElementRef;

  activeCase: any = null;
  loading = true;
  submitting = false;
  submittingComment = false;
  canEdit = false;
  isTenantAdmin = false;
  userId: string;
  userName: string;

  commentDraft = '';

  locations: any[] = [];
  users: TenantUser[] = [];

  // Materialized for the assignment pickers rather than bound straight off the assignableUsers
  // getter, which returns a fresh array every change-detection pass and would needlessly
  // invalidate an OnPush child on every tick.
  assigneeOptions: FilterDropdownOption[] = [];
  locationOptions: FilterDropdownOption[] = [];
  statusOptions: FilterDropdownOption[] = [];

  updateModel = {
    userStatusId: null,
    message: ''
  };

  // Opening state entered before a case exists. It rides the single create call
  // (CreateCaseRequest) — the server materializes the assignment, initial status,
  // and an optional first comment in one shot.
  // assignedLocationId is null, not '', so the picker's "None" row reads as selected on a fresh
  // form. save() coerces both with `|| ''`, so the request body is unchanged.
  createModel = {
    assignedUserId: null,
    assignedLocationId: null,
    comment: ''
  };
  userMadeUpdates: boolean = false;

  userStatuses: ReferenceData[] = [];

  private destroy$ = new Subject();

  constructor(
    private alertDetailService: AlertDetailService,
    private userService: UserService,
    private translate: TranslateService,
    private alertService: AlertService,
    private authService: AuthService,
    private authorizationService: AuthorizationService,
    private cdr: ChangeDetectorRef
  ) { }

  // The context carries the assignee filter, and a host may only learn what to filter by
  // after the panel is already up — the Issues sheet holds a row that does not carry the
  // vehicle's groups, so it fetches them beside the open drawer. Without this the options
  // keep whatever scope they were first built with, and a late scope changes nothing.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.context && !changes.context.firstChange) this.rebuildAssigneeOptions();
  }

  ngOnInit(): void {
    if (!this.context) {
      this.context = alertCaseWorkflowContext(this.alertDetailService, this.alert);
    }

    this.canEdit = this.authorizationService.hasRight(
      this.authService.user?.profile,
      MaintainVehicle
    );
    this.isTenantAdmin = this.authorizationService.hasRight(
      this.authService.user?.profile,
      MaintainTenant
    );

    this.getCurrentUser$.pipe(take(1)).subscribe((user: TenantUser) => {
      this.userId = user?.id;
      this.userName = `${user?.firstName} ${user?.lastName}`.trim() || user?.id;
      // Self is one of the always-visible assignees, so the list depends on this landing.
      this.rebuildAssigneeOptions();
      this.cdr.markForCheck();
    });

    this.currentTenantUsers$.pipe(takeUntil(this.destroy$)).subscribe(users => {
      this.users = users || [];
      this.rebuildAssigneeOptions();
      this.cdr.markForCheck();
    });

    this.loadUserStatuses();
    this.loadLocations();
    this.checkExistingCase(this.preload$);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  loadUserStatuses(): void {
    this.alertService.getCaseUserStatuses().pipe(take(1)).subscribe((res: ReferenceData[]) => {
      this.userStatuses = res || [];
      // Deliberately NOT sorted: status order is the workflow's, not alphabetical. Names are
      // translation keys, so resolve them here — the picker renders its label as plain text.
      this.statusOptions = this.userStatuses.map(s => ({
        value: s.id,
        label: this.translate.instant(s.name)
      }));
      if (!this.updateModel.userStatusId && this.userStatuses.length > 0) {
        this.updateModel.userStatusId = this.userStatuses[0]?.id;
      }
      this.cdr.markForCheck();
    });
  }

  loadLocations(): void {
    this.userService.getUserLocationGroups()
      .pipe(take(1))
      .subscribe(res => {
        this.locations = (res || []).sort((a, b) => a.name.localeCompare(b.name));
        this.locationOptions = this.locations.map(l => ({ value: l.id, label: l.name }));
        this.cdr.markForCheck();
      });
  }

  // The picker filters on a single label string, so the name is joined here — otherwise typing a
  // last name would never match. "None" is not an entry: it comes from the picker's allLabel row.
  // Nameless users fall back to their email; never to the id, which would surface a raw ObjectId
  // as the option text.
  private rebuildAssigneeOptions(): void {
    this.assigneeOptions = this.assignableUsers
      .map(u => ({
        value: u.id,
        label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || ''
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private transformMessage(message: string): string {
    if (!message || !message.startsWith('@')) return message;

    return message.split('; ').map(part => {
      if (!part.startsWith('@')) return part;

      const [key, paramsRaw] = part.split(':');
      const params = paramsRaw ? paramsRaw.split('|') : [];

      const mappedParams = params.map(p => {
        const user = this.users.find(u => u.id === p);
        if (user) return `${user.firstName} ${user.lastName}`.trim();

        const loc = this.locations.find(l => l.id === p);
        if (loc) return loc.name;

        const status = this.userStatuses.find(s => s.id.toString() === p);
        if (status) return status.name;

        return p.startsWith('@') ? this.translate.instant(p) : p;
      });

      let translated = this.translate.instant(key);
      mappedParams.forEach((val, idx) => {
        translated = translated.replace(`{${idx}}`, val);
      });

      return translated;
    }).join('; ');
  }

  get combinedHistory(): any[] {
    if (!this.activeCase) return [];

    const currentStatusUser = this.users.find(u => u.id === this.activeCase.status?.userId);
    const currentEntry = {
      ...this.activeCase.status,
      userName: currentStatusUser ? `${currentStatusUser.firstName} ${currentStatusUser.lastName}`.trim() : this.activeCase.status?.userId,
      message: this.transformMessage(this.activeCase.status?.message),
      isCurrent: true
    };

    const historyEntries = (this.activeCase.statusHistory || []).map(h => {
      const foundUser = this.users.find(u => u.id === h.userId);
      return {
        ...h,
        message: this.transformMessage(h.message),
        userName: foundUser
          ? `${foundUser.firstName} ${foundUser.lastName}`.trim()
          : h.userName || h.userId
      };
    });

    // Operator comments ride the same chronological feed as status entries but
    // render distinctly (author avatar + body). Soft-deleted comments (a real
    // deletedDateTimeUtc; MinValue ≈ year 1 = not deleted) are excluded.
    const commentEntries = (this.activeCase.comments || [])
      .filter(c => !this.isCommentDeleted(c))
      .map(c => {
        const author = this.users.find(u => u.id === c.userId);
        const authorName = author ? `${author.firstName} ${author.lastName}`.trim() : c.userId;
        return {
          ...c,
          isComment: true,
          userName: authorName,
          authorInitials: this.initialsOf(authorName)
        };
      });

    const allEntries = [currentEntry, ...historyEntries, ...commentEntries];

    // Reversed sort direction (a - b) to put the newest entries at the bottom of the list like SMS
    return allEntries.sort((a, b) =>
      new Date(a.createDateTimeUtc).getTime() - new Date(b.createDateTimeUtc).getTime()
    );
  }

  private isCommentDeleted(comment: any): boolean {
    if (!comment?.deletedDateTimeUtc) return false;
    const t = Date.parse(comment.deletedDateTimeUtc);
    if (isNaN(t)) return false;
    return new Date(t).getUTCFullYear() > 1;
  }

  private initialsOf(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '';
    const letters = parts.map(p => p.charAt(0)).filter(Boolean);
    return (letters[0] + (letters.length > 1 ? letters[letters.length - 1] : '')).toUpperCase();
  }

  canDeleteComment(entry: any): boolean {
    // Optimistic (not-yet-confirmed) comments have no server id to delete.
    if (!entry?.isComment || (entry.id || '').startsWith('temp-')) return false;
    return entry.userId === this.userId || this.isTenantAdmin;
  }

  // Stable identity for the combined-history *ngFor. combinedHistory is a getter that returns
  // freshly-mapped objects every change-detection cycle, so without trackBy Angular destroys and
  // recreates every bubble each CD tick — under continuous CD (e.g. background errors) that
  // detaches the comment delete button faster than it can be clicked. Keying by entry id keeps
  // the DOM stable.
  trackHistory(_index: number, h: any): string {
    return (h?.isComment ? 'c:' : 's:') + (h?.id || h?.createDateTimeUtc || '');
  }

  get hasChanges(): boolean {
    if (!this.activeCase) return false;
    const statusChanged = this.updateModel.userStatusId !== this.activeCase.status?.userStatusId;
    const messageTyped = this.updateModel.message.trim().length > 0;
    const userChanged = this.activeCase.assignedUserId !== this.activeCase.originalAssignedUserId;
    const locationChanged = this.activeCase.assignedLocationId !== this.activeCase.originalAssignedLocationId;

    return statusChanged || messageTyped || userChanged || locationChanged;
  }

  // What a close would throw away. The drawer guard and the Close button both read it.
  // The two editors save separately, so each is judged on its own. A case save in flight
  // still holds the values it carries until its poll returns, so those are exempt while it
  // runs. The comment box clears its draft before its request goes out, so a draft that is
  // still there is always unsent, whatever else is in flight.
  get hasUnsavedEdits(): boolean {
    const commentPending = this.commentDraft.trim().length > 0;
    if (this.submitting) return commentPending;
    const caseEdits = this.activeCase
      ? this.hasChanges
      : this.createModel.comment.trim().length > 0
        || this.createModel.assignedUserId != null
        || this.createModel.assignedLocationId != null;
    return caseEdits || commentPending;
  }

  // Assignability is origin-specific (see CaseWorkflowContext): alert cases
  // require alert visibility (groups + My View tags); issue cases are
  // asset-group-scoped only.
  get assignableUsers(): TenantUser[] {
    const eligible = this.context ? this.context.eligibleUsers(this.users) : [...this.users];
    // Always keep two users visible even if they don't pass the filter, so the
    // mat-select value still renders / isn't wiped:
    //   (a) a pre-existing assignee (e.g. their My View changed after assignment), and
    //   (b) the current user (self) — covers AlertOnly/ExternalUser callers whose /users
    //       list is just themselves and who would otherwise be filtered to an empty list.
    for (const id of [this.activeCase?.assignedUserId, this.userId]) {
      if (id && !eligible.some(u => u.id === id)) {
        const u = this.users.find(x => x.id === id);
        if (u) eligible.push(u);
      }
    }
    return eligible;
  }

  // `source` (the warmed read) is for the first load only. Refreshes and pollForCase must hit the
  // network, or a poll would re-read one cached response and never see the 202 write land.
  checkExistingCase(source?: Observable<any>): void {
    this.loading = true;
    const read$ = source
      // Warmed before the user opened the panel, so letting a stale failure through would show the
      // create form over an existing case and invite a duplicate.
      ? source.pipe(catchError(() => this.context.getActiveCase()))
      : this.context.getActiveCase();

    read$
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.setActiveCase(res && res.length > 0 ? res[0] : null);
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => { this.loading = false; this.cdr.markForCheck(); }
      });
  }

  // Adopt a case and snapshot the assignment baselines hasChanges compares against. save() calls
  // this with the value its create poll already returned, which saves a redundant GET.
  private setActiveCase(caseItem: any): void {
    this.activeCase = caseItem ?? null;
    if (this.activeCase) {
      this.updateModel.userStatusId = this.activeCase.status?.userStatusId;
      // Collapse "" to null on the value and its baseline together. The picker's "None" row
      // selects null, so an unassigned case served as "" would otherwise leave the row unticked
      // and read as a change the moment it was re-picked — enabling Update with nothing to save.
      this.activeCase.assignedUserId = this.activeCase.assignedUserId || null;
      this.activeCase.assignedLocationId = this.activeCase.assignedLocationId || null;
      this.activeCase.originalAssignedUserId = this.activeCase.assignedUserId;
      this.activeCase.originalAssignedLocationId = this.activeCase.assignedLocationId;
      this.rebuildAssigneeOptions();
    }
    setTimeout(() => this.scrollToBottom(), 100);
  }

  // First-time creation from the form. The whole opening intent — origin,
  // assignment, initial status, and an optional first comment — rides one
  // CreateCaseRequest; the server materializes it (including the @openedNewCase
  // status entry and the comment, authored by the creator). No follow-up PUT.
  save(): void {
    this.submitting = true;

    const request = {
      ...this.context.createReference(),
      assignedUserId: this.createModel.assignedUserId || '',
      assignedLocationId: this.createModel.assignedLocationId || '',
      // 0 = let the server pick the default open status (the contract sentinel). Never send null:
      // UserStatusId is a non-nullable int server-side, so null 400s.
      userStatusId: this.updateModel.userStatusId ?? 0,
      openingComment: this.createModel.comment.trim()
    };

    this.writeThenPoll(
      this.alertDetailService.createCase(request),
      // 30s cap for the issue-origin chain: load issue -> dup-check -> create -> number -> read.
      () => this.pollForCase(c => !!c, 30000)
    )
      .subscribe({
        next: (caseItem) => {
          // Found: hydrate straight from the polled value (no redundant GET). Capped out: one last look.
          if (caseItem) this.setActiveCase(caseItem);
          else this.checkExistingCase();
          this.createModel.comment = '';
          this.submitting = false;
          this.userMadeUpdates = true;
          this.caseWritten.emit({
            caseId: this.activeCase?.id ?? '',
            assignedUserId: request.assignedUserId,
            assignedLocationId: request.assignedLocationId,
          });
          this.cdr.markForCheck();
        },
        error: () => { this.submitting = false; this.cdr.markForCheck(); }
      });
  }

  updateCase(): void {
    this.submitting = true;

    const auditLogs: string[] = [];
    if (!this.updateModel.message.trim()) {
      if (this.updateModel.userStatusId !== this.activeCase.status?.userStatusId) {
        const oldStatusKey = this.userStatuses.find(s => s.id === this.activeCase.status?.userStatusId)?.name || '@unknown';
        const newStatusKey = this.userStatuses.find(s => s.id === this.updateModel.userStatusId)?.name || '@unknown';
        auditLogs.push(`@changedStatusMessage:${oldStatusKey}|${newStatusKey}`);
      }

      if (this.activeCase.assignedUserId !== this.activeCase.originalAssignedUserId) {
        const userId = this.activeCase.assignedUserId || '@unassigned';
        auditLogs.push(`@assignedUserMessage:${userId}`);
      }

      if (this.activeCase.assignedLocationId !== this.activeCase.originalAssignedLocationId) {
        const locId = this.activeCase.assignedLocationId || '@unassigned';
        auditLogs.push(`@assignedLocationMessage:${locId}`);
      }
    }

    const finalMessage = this.updateModel.message.trim() || auditLogs.join('; ');

    const request = {
      caseId: this.activeCase.id,
      userId: this.userId,
      assignedUserId: this.activeCase.assignedUserId,
      assignedLocationId: this.activeCase.assignedLocationId || "",
      status: {
        userId: this.userId,
        userStatusId: this.updateModel.userStatusId,
        message: finalMessage
      },
      events: []
    };

    this.writeThenPoll(
      this.alertDetailService.updateCase(request),
      () => this.pollForCaseUpdate(request.status.userStatusId, finalMessage)
    )
      .subscribe({
        next: () => {
          this.updateModel.message = '';
          this.checkExistingCase();
          this.submitting = false;
          this.userMadeUpdates = true;
          this.caseWritten.emit({
            caseId: request.caseId ?? '',
            assignedUserId: request.assignedUserId || '',
            assignedLocationId: request.assignedLocationId,
          });
          this.cdr.markForCheck();
        },
        error: () => { this.submitting = false; this.cdr.markForCheck(); }
      });
  }

  private pollForCaseUpdate(
    targetStatusId: number,
    targetMessage: string,
    checkDetails: boolean = true): Observable<any> {
    return this.pollForCase(caseItem => {
      if (!checkDetails) return true;
      return caseItem.status?.userStatusId === targetStatusId && caseItem.status?.message === targetMessage;
    });
  }

  // Every case write is 202/async: fire the write, then poll the read until it reflects it. Emits
  // exactly once — the case, or null if the poll capped out — so callers reset their spinner in
  // `next` unconditionally. Destroy ends it, so a capped poll can't keep GETting into a dead view
  // (collapsing Coordination destroys this component).
  private writeThenPoll(write$: Observable<any>, poll: () => Observable<any>): Observable<any> {
    return write$.pipe(
      take(1),
      switchMap(() => poll()),
      // Order matters: defaultIfEmpty turns a capped poll into that null, while the takeUntil below
      // has to end the stream silently — swapping them would emit null into a destroyed view.
      defaultIfEmpty(null),
      takeUntil(this.destroy$)
    );
  }

  // Poll the origin's active case (2s cadence) until `expectation` holds — the write endpoints
  // are all 202/async, so reads lag the request. Caps at `timeoutMs` (default 10s), after which
  // the stream completes without emitting; writeThenPoll normalizes that to a null emission.
  private pollForCase(expectation: (caseItem: any) => boolean, timeoutMs: number = 10000): Observable<any> {
    return timer(0, 2000).pipe(
      switchMap(() => this.context.getActiveCase()),
      map(res => (res && res.length > 0) ? res[0] : null),
      filter(caseItem => !!caseItem && expectation(caseItem)),
      take(1),
      takeUntil(timer(timeoutMs))
    );
  }

  onCommentEnter(event: any): void {
    if (event.shiftKey) return;
    event.preventDefault();
    if (this.commentDraft.trim() && !this.submittingComment) {
      this.addComment();
    }
  }

  // 202 write: optimistically append, then poll until the server's case carries
  // the comment and adopt server truth.
  addComment(): void {
    const body = this.commentDraft.trim();
    if (!body || !this.activeCase) return;

    this.submittingComment = true;
    this.activeCase.comments = [
      ...(this.activeCase.comments || []),
      {
        id: `temp-${Date.now()}`,
        userId: this.userId,
        message: body,
        createDateTimeUtc: new Date().toISOString(),
        deletedDateTimeUtc: '0001-01-01T00:00:00'
      }
    ];
    this.commentDraft = '';
    setTimeout(() => this.scrollToBottom(), 100);

    this.writeThenPoll(
      this.alertDetailService.addCaseComment(this.activeCase.id, body),
      () => this.pollForCase(c =>
        (c.comments || []).some(x => x.message === body && !this.isCommentDeleted(x)))
    )
      .subscribe({
        next: () => {
          this.checkExistingCase();
          this.submittingComment = false;
          this.userMadeUpdates = true;
          this.cdr.markForCheck();
        },
        error: () => { this.submittingComment = false; this.cdr.markForCheck(); }
      });
  }

  deleteComment(entry: any): void {
    if (!entry?.id || !this.activeCase) return;

    this.submittingComment = true;
    const commentId = entry.id;
    // Optimistically soft-delete so the feed drops it immediately (mirrors addComment's optimism
    // and doesn't hang on the async round-trip). The poll + refetch adopt server truth; a genuine
    // failure rolls back, and if the server never actually deleted it the refetch brings it back —
    // so this can't mask a broken delete.
    const prevComments = this.activeCase.comments;
    this.activeCase.comments = (this.activeCase.comments || []).map(c =>
      c.id === commentId ? { ...c, deletedDateTimeUtc: new Date().toISOString() } : c);
    this.cdr.markForCheck();

    this.writeThenPoll(
      this.alertDetailService.deleteCaseComment(this.activeCase.id, commentId),
      () => this.pollForCase(c => {
        const found = (c.comments || []).find(x => x.id === commentId);
        return !found || this.isCommentDeleted(found);
      })
    )
      .subscribe({
        next: () => {
          this.checkExistingCase();
          this.submittingComment = false;
          this.userMadeUpdates = true;
          this.cdr.markForCheck();
        },
        error: () => {
          if (this.activeCase) this.activeCase.comments = prevComments;
          this.submittingComment = false;
          this.cdr.markForCheck();
        }
      });
  }

  copyChat(): void {
    const lines = this.combinedHistory.map(h => {
      const user = h.userName || h.userId || 'System';
      const date = new Date(h.createDateTimeUtc).toLocaleString([], {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
      const msg = h.message || '(no message)';
      return `[${date}] ${user}: ${msg}`;
    });
    navigator.clipboard.writeText(lines.join('\n'));
  }

  closeClicked(): void {
    if (this.hasUnsavedEdits && !confirm(this.translate.instant('@confirmDiscardChanges'))) return;
    this.close$.emit(this.userMadeUpdates);
  }

  private scrollToBottom(): void {
    if (this.chatScrollArea) {
      const el = this.chatScrollArea.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }
}
